import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { api, apiError } from "@/services/api"
import type { JobDescription, OrgUnit, Position, PositionCreate, Workplace } from "@/types"

interface PositionSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null = Neuanlage */
  position: Position | null
  workplaces: Workplace[]
  orgUnits: OrgUnit[]
  jobDescriptions: JobDescription[]
  canWrite: boolean
  /** Vorbelegte Organisationseinheit bei Neuanlage ("Stelle hinzufügen" am
   * Organigramm-Knoten). Nur bei position === null wirksam, bleibt änderbar. */
  defaultOrgUnitId?: string | null
}

/** Detail-Sheet einer Stelle mit Edit-Formular (Grid+Sheet-Muster aus dem Skill). */
export function PositionSheet({
  open,
  onOpenChange,
  position,
  workplaces,
  orgUnits,
  jobDescriptions,
  canWrite,
  defaultOrgUnitId = null,
}: PositionSheetProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState("")
  const [workplaceId, setWorkplaceId] = useState("")
  const [orgUnitId, setOrgUnitId] = useState("")
  const [jobDescriptionId, setJobDescriptionId] = useState("")
  const [surchargeRate, setSurchargeRate] = useState("")
  const [visibleRoles, setVisibleRoles] = useState("")

  useEffect(() => {
    if (!open) return
    setName(position?.name ?? "")
    setWorkplaceId(position?.workplaceId ?? "")
    setOrgUnitId(position?.orgUnitId ?? defaultOrgUnitId ?? "")
    setJobDescriptionId(position?.jobDescriptionId ?? "")
    setSurchargeRate(position?.surchargeRate != null ? String(position.surchargeRate) : "")
    setVisibleRoles(position?.visibleRoles?.join(", ") ?? "")
  }, [open, position, defaultOrgUnitId])

  const mutation = useMutation({
    mutationFn: (body: PositionCreate) =>
      position
        ? api.organization.positions.update(position.id, body)
        : api.organization.positions.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["positions"] })
      toast.success(position ? "Stelle aktualisiert" : "Stelle angelegt")
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function submit() {
    const roles = visibleRoles
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean)
    mutation.mutate({
      name: name.trim(),
      workplaceId: workplaceId ? Number(workplaceId) : null,
      orgUnitId: orgUnitId ? Number(orgUnitId) : null,
      jobDescriptionId: jobDescriptionId ? Number(jobDescriptionId) : null,
      surchargeRate: surchargeRate !== "" ? surchargeRate : null,
      visibleRoles: roles.length > 0 ? roles : null,
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="position" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{position ? position.name : "Neue Stelle"}</SheetTitle>
          <SheetDescription>
            {position ? "Details und Bearbeitung der Stelle." : "Neue Stelle anlegen."}
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="pos-name">Name</Label>
            <Input
              id="pos-name"
              value={name}
              disabled={!canWrite}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-workplace">Arbeitsplatz</Label>
            <Select
              id="pos-workplace"
              value={workplaceId}
              disabled={!canWrite}
              onChange={(e) => setWorkplaceId(e.target.value)}
            >
              <option value="">— kein Arbeitsplatz —</option>
              {workplaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-orgunit">Organisationseinheit</Label>
            <Select
              id="pos-orgunit"
              value={orgUnitId}
              disabled={!canWrite}
              onChange={(e) => setOrgUnitId(e.target.value)}
            >
              <option value="">— keine Einheit —</option>
              {orgUnits.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-jobdesc">Stellenbeschreibung</Label>
            <Select
              id="pos-jobdesc"
              value={jobDescriptionId}
              disabled={!canWrite}
              onChange={(e) => setJobDescriptionId(e.target.value)}
            >
              <option value="">— keine —</option>
              {jobDescriptions.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-surcharge">Zulagenstundensatz (€/h)</Label>
            <Input
              id="pos-surcharge"
              type="number"
              step="0.01"
              min="0"
              value={surchargeRate}
              disabled={!canWrite}
              onChange={(e) => setSurchargeRate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-roles">Sichtbare Rollen (kommagetrennt)</Label>
            <Input
              id="pos-roles"
              placeholder="z. B. produktion:leitung, technik:leitung"
              value={visibleRoles}
              disabled={!canWrite}
              onChange={(e) => setVisibleRoles(e.target.value)}
            />
            {position?.visibleRoles && position.visibleRoles.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {position.visibleRoles.map((r) => (
                  <Badge key={r} variant="secondary">
                    {r}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        {canWrite && (
          <SheetFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button disabled={name.trim() === "" || mutation.isPending} onClick={submit}>
              Speichern
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  )
}
